import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Truck, Sliders } from "react-feather";

const AddEdit_VehicleTypeMaster = () => {
  const API_URL_SAVE = "VehicleTypeMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/VehicleType/Id";
  const API_URL = API_WEB_URLS.MASTER + "/0/token/VehicleCategory/Id/0";
  
  const [state, setState] = useState({
    id: 0,
    vehicleCategoryList: [],
    formData: {
      Name: "",
      WeightCapacity: "",
      F_VehicleCategory: "",
      PhotoIcon: "",
    },
    photoPreview: null,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;
    Fn_FillListData(dispatch, setState, "VehicleCategoryList", API_URL);
    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    Name: Yup.string()
      .required("Vehicle type name is required")
      .min(2, "Vehicle type name must be at least 2 characters")
      .max(100, "Vehicle type name must be less than 100 characters"),
    F_VehicleCategory: Yup.string()
      .required("Vehicle category is required"),
    WeightCapacity: Yup.number()
      .typeError("Weight capacity must be a valid number")
      .required("Weight capacity is required")
      .min(0, "Weight capacity cannot be negative"),
    PhotoIcon: Yup.mixed()
      .required("Photo icon is required"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("Name", values.Name || "");
    formData.append("F_VehicleCategory", values.F_VehicleCategory || "");
    formData.append("RangeFrom", 0);
    formData.append("RangeTo", values.WeightCapacity ?? 0);
    formData.append("WeightCapacity", values.WeightCapacity ?? 0);
    if (values.PhotoIcon && values.PhotoIcon instanceof File) {
      formData.append("PhotoIcon", values.PhotoIcon);
    }
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "vehicletypeid",
      navigate,
      "/VehicleTypeMaster"
    );
  };

  return (
    <div style={{ padding: "6px 8px", maxWidth: 900, margin: "0 auto" }}>
      <Card className="border shadow-none" style={{ marginBottom: 0 }}>
        <CardBody className="p-2" style={{ padding: "8px 12px !important" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #dee2e6" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Vehicle Type Master</span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button type="button" size="sm" color="light" className="py-1 px-2" style={{ fontSize: 12 }} onClick={() => navigate("/VehicleTypeMaster")}><ArrowLeft size={12} className="me-1" />Back</Button>
              <Button type="submit" form="vehicletype-form" size="sm" color="primary" className="py-1 px-2" style={{ fontSize: 12 }}><Save size={12} className="me-1" />Save</Button>
            </div>
          </div>
          <Formik 
                  initialValues={{
                    Name: state.formData?.Name ?? "",
                    F_VehicleCategory: state.formData?.F_VehicleCategory ?? "",
                    WeightCapacity: state.formData?.WeightCapacity ?? "",
                    PhotoIcon: state.formData?.PhotoIcon ?? "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form id="vehicletype-form">
                      <div className="form-group mb-3">
                        <label htmlFor="F_VehicleCategory" className="master-form-label">
                          <Truck size={16} className="me-2" />
                          Vehicle Category <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            as="select"
                            className={`master-form-input ${errors.F_VehicleCategory && touched.F_VehicleCategory ? 'is-invalid' : ''}`}
                            name="F_VehicleCategory"
                          >
                            <option value="">Select Vehicle Category</option>
                            {state.VehicleCategoryList && state.VehicleCategoryList.map((category) => (
                              <option key={category.Id} value={category.Id}>
                                {category.Name}
                              </option>
                            ))}
                          </Field>
                          <div className="input-icon">
                            <Truck size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="F_VehicleCategory" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Select the vehicle category (e.g., Truck, 3 Wheel, 2 Wheel)
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="Name" className="master-form-label">
                          <Truck size={16} className="me-2" />
                          Vehicle Type Name <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            innerRef={nameRef}
                            className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                            type="text"
                            name="Name"
                            placeholder="Enter vehicle type name (e.g., Car, Truck, Van)"
                          />
                          <div className="input-icon">
                            <Truck size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="Name" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Enter the type of vehicle (e.g., Car, Motorcycle, Truck, Bus, Van)
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="WeightCapacity" className="master-form-label">
                          <Sliders size={16} className="me-2" />
                          Weight Capacity <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            className={`master-form-input ${errors.WeightCapacity && touched.WeightCapacity ? 'is-invalid' : ''}`}
                            type="number"
                            name="WeightCapacity"
                            placeholder="Enter weight capacity"
                            min="0"
                          />
                          <div className="input-icon">
                            <Sliders size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="WeightCapacity" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Specify the weight capacity for this vehicle type.
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="PhotoIcon" className="master-form-label">
                          Photo Icon <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field name="PhotoIcon">
                            {({ field, form }) => (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className={`master-form-input ${errors.PhotoIcon && touched.PhotoIcon ? 'is-invalid' : ''}`}
                                  onChange={e => {
                                    const file = e.target.files[0];
                                    form.setFieldValue("PhotoIcon", file);
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = ev => setState(prev => ({ ...prev, photoPreview: ev.target.result }));
                                      reader.readAsDataURL(file);
                                    } else {
                                      setState(prev => ({ ...prev, photoPreview: null }));
                                    }
                                  }}
                                />
                                {/* Show preview for new upload */}
                                {state.photoPreview && (
                                  <div style={{ marginTop: 8 }}>
                                    <img src={state.photoPreview} alt="Preview" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, border: '1px solid #ccc' }} />
                                  </div>
                                )}
                                {/* Show old image if editing and no new preview */}
                                {!state.photoPreview && state.formData.PhotoIcon && typeof state.formData.PhotoIcon === "string" && (
                                  <div style={{ marginTop: 8 }}>
                                    <img src={API_WEB_URLS.IMAGEURL + state.formData.PhotoIcon} alt="Old" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, border: '1px solid #ccc' }} />
                                  </div>
                                )}
                              </>
                            )}
                          </Field>
                        </div>
                        <ErrorMessage name="PhotoIcon" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Upload an image for the vehicle type icon.
                        </div>
                      </div>

                    </Form>
                  )}
                </Formik>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddEdit_VehicleTypeMaster;

